import { useEffect, useMemo, useState } from "react";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { withSite, formatErr } from "@/lib/api";
import { computeEntitlement } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Sparkles, ShieldCheck, Clock, Loader2, CreditCard, UserPlus } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGE = {
  haccp_site_monthly: null,
  haccp_site_quarterly: "Save 1%",
  haccp_site_annual: "Save 5%",
};

export default function Billing() {
  const { activeSite, refresh } = useSite();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [owners, setOwners] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "" });
  const [newOwnerPw, setNewOwnerPw] = useState(null);

  const ent = useMemo(() => computeEntitlement(activeSite), [activeSite]);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeSite?.id) return;
    api.get("/billing/status", withSite(activeSite.id))
      .then(({ data }) => setStatus(data))
      .catch(() => {});
  }, [activeSite]);

  const loadOwners = () => {
    if (!activeSite?.id || user?.role !== "admin") return;
    api.get("/billing/owners", withSite(activeSite.id, { site_id: activeSite.id }))
      .then(({ data }) => setOwners(data || []))
      .catch(() => {});
  };
  useEffect(loadOwners, [activeSite, user]);

  const inviteOwner = async (e) => {
    e.preventDefault();
    if (!activeSite?.id || !invite.email || !invite.name) return;
    setBusy("invite");
    try {
      const { data } = await api.post("/billing/invite-owner", {
        site_id: activeSite.id, email: invite.email, name: invite.name,
      }, withSite(activeSite.id));
      setNewOwnerPw({ email: data.email, password: data.temp_password });
      setInvite({ email: "", name: "" });
      loadOwners();
      toast.success("Billing owner invited");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't invite owner");
    } finally {
      setBusy("");
    }
  };

  const removeOwner = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      loadOwners();
      toast.success("Billing owner removed");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't remove owner");
    }
  };

  const subscribe = async (lookup_key) => {
    if (!activeSite?.id) return;
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        site_id: activeSite.id,
        origin_url: window.location.origin,
      }, withSite(activeSite.id));
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Couldn't start checkout");
        setBusy("");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't start checkout");
      setBusy("");
    }
  };

  const openPortal = async () => {
    if (!activeSite?.id) return;
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        site_id: activeSite.id,
        origin_url: window.location.origin,
      }, withSite(activeSite.id));
      if (data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        toast.error("Couldn't open billing portal");
        setBusy("");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't open billing portal");
      setBusy("");
    }
  };

  const changePlan = async (lookup_key) => {
    if (!activeSite?.id) return;
    setBusy(lookup_key);
    try {
      await api.post("/billing/change-plan", { site_id: activeSite.id, lookup_key }, withSite(activeSite.id));
      const { data } = await api.get("/billing/status", withSite(activeSite.id));
      setStatus(data);
      toast.success("Plan updated — the prorated difference has been applied.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't change plan");
    } finally {
      setBusy("");
    }
  };

  const active = status?.subscription_status === "active";
  const currentPlan = status?.subscription_plan;
  const canBill = user?.role === "admin" || user?.role === "billing_owner";
  const canManage = canBill && (status?.has_customer || active || status?.subscription_status === "past_due");

  return (
    <div className="space-y-8 max-w-5xl" data-testid="billing-page">
      <header>
        <div className="label-eyebrow">Subscription</div>
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-eucalyptus-900 mt-1">
          Billing &amp; Plans
        </h1>
        <p className="text-charcoal-soft mt-2 max-w-2xl">
          Each site runs on its own subscription. You get a 30-day free trial, then pick a plan to keep
          creating records and printing labels for <b>{activeSite?.name || "this site"}</b>.
        </p>
      </header>

      {/* Current status */}
      <div className="surface p-5 flex flex-wrap items-center gap-4" data-testid="billing-status">
        {active ? (
          <span className="inline-flex items-center gap-2 text-emerald-700 font-semibold">
            <ShieldCheck className="w-5 h-5" /> Active subscription
            {status?.subscription_plan && <span className="text-charcoal-soft font-normal">· {status.subscription_plan.replace("haccp_site_", "")}</span>}
          </span>
        ) : ent.expired ? (
          <span className="inline-flex items-center gap-2 text-red-700 font-semibold" data-testid="billing-status-expired">
            <Clock className="w-5 h-5" /> Free trial ended — subscribe to unlock creating records &amp; printing.
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 text-ochre-700 font-semibold" data-testid="billing-status-trial">
            <Sparkles className="w-5 h-5" /> Free trial · {ent.daysLeft} day{ent.daysLeft === 1 ? "" : "s"} left
          </span>
        )}
        {status?.current_period_end && active && (
          <span className="text-xs text-charcoal-soft">Renews {new Date(status.current_period_end).toLocaleDateString()}</span>
        )}
        {canManage && (
          <Button
            data-testid="billing-manage-btn"
            variant="outline"
            className="ml-auto gap-2"
            disabled={busy === "portal"}
            onClick={openPortal}
          >
            {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Manage / cancel
          </Button>
        )}
      </div>

      {!canBill && (
        <div className="surface p-4 text-sm text-charcoal-soft" data-testid="billing-not-admin">
          Only an admin or billing owner can manage this site's subscription. Please ask them to subscribe.
        </div>
      )}

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-5" data-testid="billing-plans">
        {plans.map((p) => (
          <div
            key={p.lookup_key}
            data-testid={`plan-card-${p.lookup_key}`}
            className="surface p-6 flex flex-col gap-4 relative border-2 border-transparent hover:border-eucalyptus-700/40 transition-colors"
          >
            {PLAN_BADGE[p.lookup_key] && (
              <span className="absolute -top-3 right-4 bg-ochre-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                {PLAN_BADGE[p.lookup_key]}
              </span>
            )}
            <div>
              <div className="font-display font-bold text-xl text-eucalyptus-900">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-charcoal">{p.price}</span>
                <span className="text-charcoal-soft text-sm">AUD / {p.interval}</span>
              </div>
            </div>
            <ul className="text-sm text-charcoal-soft space-y-1.5 flex-1">
              <li className="flex gap-2"><Check className="w-4 h-4 text-eucalyptus-700 shrink-0" /> Unlimited records &amp; label printing</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-eucalyptus-700 shrink-0" /> Full HACCP logs &amp; PDF/CSV export</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-eucalyptus-700 shrink-0" /> Print Hub &amp; Canvas Designer</li>
            </ul>
            {active && currentPlan === p.lookup_key ? (
              <Button
                data-testid={`plan-current-${p.lookup_key}`}
                disabled
                className="w-full bg-emerald-600 text-white gap-2 disabled:opacity-100"
              >
                <Check className="w-4 h-4" /> Current plan
              </Button>
            ) : active ? (
              <Button
                data-testid={`plan-switch-${p.lookup_key}`}
                disabled={!canBill || !!busy}
                onClick={() => changePlan(p.lookup_key)}
                variant="outline"
                className="w-full gap-2 border-eucalyptus-700 text-eucalyptus-900"
              >
                {busy === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Switch to {p.name}
              </Button>
            ) : (
              <Button
                data-testid={`plan-subscribe-${p.lookup_key}`}
                disabled={!canBill || !!busy}
                onClick={() => subscribe(p.lookup_key)}
                className="w-full bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
              >
                {busy === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Choose {p.name}
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-charcoal-soft">Secure checkout by Stripe. Cancel anytime.</p>

      {/* Owner handover — invite a finance/billing-only user */}
      {isAdmin && (
        <div className="surface p-6 space-y-5" data-testid="billing-owners">
          <div>
            <div className="label-eyebrow">Billing owners</div>
            <h2 className="text-lg font-display font-bold text-eucalyptus-900 mt-1">Hand billing to finance</h2>
            <p className="text-sm text-charcoal-soft mt-1 max-w-2xl">
              Invite someone who can manage <b>only</b> this site's subscription — no access to records, dashboards or labels.
            </p>
          </div>

          {newOwnerPw && (
            <div className="rounded-lg bg-eucalyptus-50 border border-eucalyptus-700/30 p-4 text-sm" data-testid="owner-temp-password">
              <div className="font-semibold text-eucalyptus-900">Share these one-time login details with {newOwnerPw.email}:</div>
              <div className="mt-1 font-mono">Email: {newOwnerPw.email}</div>
              <div className="font-mono">Temporary password: <b data-testid="owner-temp-password-value">{newOwnerPw.password}</b></div>
              <div className="text-xs text-charcoal-soft mt-1">They can change it after first sign-in. This won't be shown again.</div>
            </div>
          )}

          <form onSubmit={inviteOwner} className="flex flex-wrap items-end gap-3" data-testid="owner-invite-form">
            <div className="min-w-[180px]">
              <Label className="text-xs">Name</Label>
              <Input data-testid="owner-invite-name" value={invite.name} onChange={(e) => setInvite((s) => ({ ...s, name: e.target.value }))} placeholder="Finance team" />
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs">Email</Label>
              <Input data-testid="owner-invite-email" type="email" value={invite.email} onChange={(e) => setInvite((s) => ({ ...s, email: e.target.value }))} placeholder="finance@company.com" />
            </div>
            <Button data-testid="owner-invite-submit" type="submit" disabled={busy === "invite"} className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2">
              {busy === "invite" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Invite owner
            </Button>
          </form>

          {owners.length > 0 && (
            <ul className="divide-y divide-eucalyptus-700/10" data-testid="owner-list">
              {owners.map((o) => (
                <li key={o.id} className="flex items-center justify-between py-2 text-sm" data-testid={`owner-row-${o.id}`}>
                  <span><b>{o.name}</b> · {o.email}</span>
                  <button data-testid={`owner-remove-${o.id}`} onClick={() => removeOwner(o.id)} className="text-red-600 hover:text-red-800 text-xs font-semibold">
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
